import { isEqual } from "lodash";
import { useState, useMemo, useCallback, useEffect } from "react";

import { LegislationsQuickFilter } from "./interfaces";

import { LegislationDTO } from "../../../models/legislations";
import { LegislationsApiService } from "../../../services/legislations/legislations-api.service";

export const useLegislationsQuickFilter = (allLegislationsAsDefault = false) => {
  const [legislations, setCurrLegislations] = useState<LegislationDTO[]>([]);

  const [quickfilterIsReady, setQuickfilterIsReady] = useState(false);

  const loadItemCallback = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await new LegislationsApiService().loadItems(
        {
          ids: [],
        },
        undefined,
        undefined, // so that service API default sort is applied
        { pageNumber: 0, pageSize: 1000 },
        undefined,
        60,
        signal
      );
      if (!response.error) {
        setCurrLegislations(response.items);
        setQuickFilter({
          legislations: {
            legislationsIds: allLegislationsAsDefault ? [] : [...(response.items[0]?.id ? [response.items[0].id] : [])],
          },
        });
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setQuickfilterIsReady(true);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    loadItemCallback(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  const [quickFilter, setQuickFilter] = useState<LegislationsQuickFilter>({
    legislations: {
      legislationsIds: allLegislationsAsDefault ? [] : [...(legislations[0]?.id ? [legislations[0].id] : [])],
    },
  });

  const selectedLegislationId = useMemo(
    () => quickFilter.legislations.legislationsIds[0],
    [quickFilter.legislations.legislationsIds]
  );

  const selectedLegislation = useMemo(
    () => legislations.filter((le) => le.id === selectedLegislationId)[0] ?? null,
    [legislations, selectedLegislationId]
  );

  const handleChangeQuickFilter = useCallback(
    (filter: keyof LegislationsQuickFilter, value: LegislationsQuickFilter["legislations"]) => {
      if (isEqual(value, quickFilter[filter])) return;

      setQuickFilter((state) => ({ ...state, [filter]: value }));
    },
    [quickFilter]
  );

  return {
    legislationsQuickFilter: quickFilter,
    setLegislationsQuickFilter: handleChangeQuickFilter,
    selectedLegislation,
    legislationQFIsReady: quickfilterIsReady,
    allLegislations: legislations,
  };
};
